import React, { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import './SettingsLayout.css';

/**
 * Mise en page « réglages » : menu vertical à gauche, zone de la section active
 * à droite. Sur mobile, le menu devient une rangée défilante au-dessus.
 *
 * Générique : la page fournit ses sections sous forme de données.
 *
 * @param {string} title - Titre de la page
 * @param {string} [description] - Sous-titre de la page
 * @param {Array<Object>} sections - Sections `{ id, label, icon, title?, description?, content, flush? }`
 *   - `icon` : élément React (Heroicons ou Ant Design), dimensionné par le CSS
 *   - `title` : titre de l'en-tête de zone (par défaut `label`)
 *   - `flush` : le contenu apporte déjà ses propres cartes ; il est affiché sans
 *     encadré pour éviter d'empiler une carte dans une carte
 * @param {string} activeId - Identifiant de la section affichée
 * @param {Function} onChange - Appelé avec l'identifiant choisi dans le menu
 */
const SettingsLayout = ({ title, description, sections, activeId, onChange }) => {
  /**
   * Sections déjà ouvertes : elles restent montées (masquées) pour conserver
   * leur état (formulaires en cours, données chargées), comme le faisaient
   * les onglets Ant Design.
   */
  const [sectionsVisitees, setSectionsVisitees] = useState(() => new Set([activeId]));
  if (!sectionsVisitees.has(activeId)) setSectionsVisitees(new Set([...sectionsVisitees, activeId]));

  return (
    <div className="settings-layout w-full px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{title}</h1>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </header>

      <div className="settings-layout__grid">
        {/* --- Menu des sections --- */}
        <nav aria-label={`Sections : ${title}`} className="settings-layout__menu">
          <ul>
            {sections.map((section) => {
              const estActive = section.id === activeId;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => onChange(section.id)}
                    aria-current={estActive ? 'page' : undefined}
                    className={`settings-layout__menu-item ${estActive ? 'is-active' : ''}`}
                  >
                    <span className="settings-layout__icon" aria-hidden="true">{section.icon}</span>
                    <span className="settings-layout__menu-label">{section.label}</span>
                    <ChevronRightIcon aria-hidden="true" className="settings-layout__chevron" />
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* --- Zone de contenu : seules les sections visitées sont montées --- */}
        <div className="min-w-0">
          {sections.filter((section) => sectionsVisitees.has(section.id)).map((section) => {
            const estActive = section.id === activeId;
            const entete = (
              <div className={`settings-layout__header ${section.flush ? 'is-flush' : ''}`}>
                <span className="settings-layout__icon settings-layout__icon--header" aria-hidden="true">{section.icon}</span>
                <div className="min-w-0">
                  <h2 className="settings-layout__title">{section.title || section.label}</h2>
                  {section.description && <p className="settings-layout__description">{section.description}</p>}
                </div>
              </div>
            );

            return (
              <section key={section.id} hidden={!estActive} aria-label={section.title || section.label}>
                {section.flush ? (
                  // Contenu déjà encadré : en-tête simple puis contenu tel quel
                  <>
                    {entete}
                    <div className="space-y-6">{section.content}</div>
                  </>
                ) : (
                  <div className="settings-layout__panel">
                    {entete}
                    <div className="settings-layout__body">{section.content}</div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
